import { SettingsType, getSettings } from '@/resource/settings';
import { SerializableObject } from '@/util/types';
import {
  GetStaticProps,
  GetStaticPropsContext,
  GetStaticPropsResult,
  PreviewData,
} from 'next';
import { ParsedUrlQuery } from 'querystring';
import z from 'zod';

type Context = GetStaticPropsContext<ParsedUrlQuery, PreviewData>;

type DefaultProps = SerializableObject & { noLayout?: boolean };

const getStaticPropsWithParams = <Schema extends z.ZodTypeAny>(
  schema: Schema
) => ({
  resolve:
    <P extends DefaultProps>(
      resolver: (
        params: z.output<Schema>,
        context: Context,
        settings: SettingsType
      ) => Promise<GetStaticPropsResult<P>>
    ): GetStaticProps<P> =>
    async (context) => {
      const params = await schema.safeParseAsync(context.params);

      if (!params.success) {
        return {
          notFound: true,
          revalidate: 10,
        };
      }

      const settings = await getSettings();
      const response = await resolver(params.data, context, settings);

      if ('props' in response) {
        return {
          ...response,
          props: {
            ...response.props,
            settings,
          },
        };
      }

      return response;
    },
});

export const staticProps = {
  params: getStaticPropsWithParams,
  resolve:
    <P extends DefaultProps>(
      resolver: (
        context: Context,
        settings: SettingsType
      ) => Promise<GetStaticPropsResult<P>>
    ): GetStaticProps<P> =>
    async (context) => {
      const settings = await getSettings();
      const response = await resolver(context, settings);

      if ('props' in response) {
        return {
          ...response,
          props: {
            ...response.props,
            settings,
          },
        };
      }

      return response;
    },
};
